import {IntlShape, defineMessages} from 'react-intl'

type RegistrationOptionsJSON = Record<string, any>
type AuthenticationOptionsJSON = Record<string, any>

export type SerializedRegistrationCredential = {
  id: string
  rawId: string
  type: PublicKeyCredential['type']
  authenticatorAttachment?: string | null
  clientExtensionResults?: AuthenticationExtensionsClientOutputs
  response: {
    clientDataJSON: string
    attestationObject: string
    transports: string[]
  }
}

export type SerializedAuthenticationCredential = {
  id: string
  rawId: string
  type: PublicKeyCredential['type']
  authenticatorAttachment?: string | null
  clientExtensionResults?: AuthenticationExtensionsClientOutputs
  response: {
    clientDataJSON: string
    authenticatorData: string
    signature: string
    userHandle?: string | null
  }
}

const base64urlToUint8Array = (value: string): Uint8Array => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = `${base64}${'='.repeat((4 - (base64.length % 4 || 4)) % 4)}`
  const binary = window.atob(padded)
  const bytes = new Uint8Array(binary.length)

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }

  return bytes
}

const bufferToBase64url = (value?: ArrayBuffer | null): string => {
  if (!value) return ''

  const bytes = new Uint8Array(value)
  let binary = ''
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte)
  })

  return window
    .btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '')
}

const normalizeTransports = (transports?: unknown): AuthenticatorTransport[] | undefined => {
  if (!Array.isArray(transports)) return undefined
  return transports.filter((transport): transport is AuthenticatorTransport => typeof transport === 'string')
}

const mapCredentialDescriptor = (descriptor: Record<string, any>) => ({
  ...descriptor,
  type: descriptor.type || 'public-key',
  id: base64urlToUint8Array(descriptor.id),
  transports: normalizeTransports(descriptor.transports),
})

export const isPasskeySupported = (): boolean => {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential && !!navigator.credentials
}

/**
 * Mensajes de passkeys. Viven fuera de un componente, asi que se declaran como
 * descriptores y los helpers reciben `intl` desde quien los llama.
 */
export const PASSKEY_MESSAGES = defineMessages({
  unavailable: {
    id: 'admin.security.passkeys.error.unavailable',
    defaultMessage:
      'Passkeys are not available in this browser or context. Use HTTPS or localhost and a supported browser.',
  },
  dismissed: {
    id: 'admin.security.passkeys.error.dismissed',
    defaultMessage: 'Passkey prompt was dismissed or timed out.',
  },
  alreadyRegistered: {
    id: 'admin.security.passkeys.error.alreadyRegistered',
    defaultMessage: 'This passkey is already registered on this device.',
  },
  cancelled: {
    id: 'admin.security.passkeys.error.cancelled',
    defaultMessage: 'Passkey flow was cancelled.',
  },
})

export const getPasskeyUnavailableMessage = (intl: IntlShape): string =>
  intl.formatMessage(PASSKEY_MESSAGES.unavailable)

export const createPasskey = async (
  optionsJson: RegistrationOptionsJSON
): Promise<SerializedRegistrationCredential> => {
  const publicKey = {
    ...optionsJson,
    challenge: base64urlToUint8Array(optionsJson.challenge),
    user: {
      ...optionsJson.user,
      id: base64urlToUint8Array(optionsJson.user.id),
    },
    excludeCredentials: Array.isArray(optionsJson.excludeCredentials)
      ? optionsJson.excludeCredentials.map(mapCredentialDescriptor)
      : undefined,
  } as unknown as PublicKeyCredentialCreationOptions

  const credential = (await navigator.credentials.create({
    publicKey,
  })) as PublicKeyCredential | null

  if (!credential) {
    throw new Error('Passkey registration was cancelled.')
  }

  const response = credential.response as AuthenticatorAttestationResponse
  const passkeyCredential = credential as PublicKeyCredential & {
    authenticatorAttachment?: string | null
  }
  const transports = typeof (response as any).getTransports === 'function'
    ? ((response as any).getTransports() as string[])
    : []

  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: passkeyCredential.authenticatorAttachment ?? null,
    clientExtensionResults: credential.getClientExtensionResults?.(),
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      attestationObject: bufferToBase64url(response.attestationObject),
      transports,
    },
  }
}

export const getPasskeyAssertion = async (
  optionsJson: AuthenticationOptionsJSON
): Promise<SerializedAuthenticationCredential> => {
  const publicKey = {
    ...optionsJson,
    challenge: base64urlToUint8Array(optionsJson.challenge),
    allowCredentials: Array.isArray(optionsJson.allowCredentials)
      ? optionsJson.allowCredentials.map(mapCredentialDescriptor)
      : undefined,
  } as unknown as PublicKeyCredentialRequestOptions

  const credential = (await navigator.credentials.get({
    publicKey,
  })) as PublicKeyCredential | null

  if (!credential) {
    throw new Error('Passkey sign-in was cancelled.')
  }

  const response = credential.response as AuthenticatorAssertionResponse
  const passkeyCredential = credential as PublicKeyCredential & {
    authenticatorAttachment?: string | null
  }

  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: passkeyCredential.authenticatorAttachment ?? null,
    clientExtensionResults: credential.getClientExtensionResults?.(),
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      authenticatorData: bufferToBase64url(response.authenticatorData),
      signature: bufferToBase64url(response.signature),
      userHandle: bufferToBase64url(response.userHandle),
    },
  }
}

export const getPasskeyErrorMessage = (
  intl: IntlShape,
  error: unknown,
  fallback: string
): string => {
  if (error && typeof error === 'object') {
    const domError = error as DOMException
    if (domError.name === 'NotAllowedError') {
      return intl.formatMessage(PASSKEY_MESSAGES.dismissed)
    }
    if (domError.name === 'InvalidStateError') {
      return intl.formatMessage(PASSKEY_MESSAGES.alreadyRegistered)
    }
    if (domError.name === 'AbortError') {
      return intl.formatMessage(PASSKEY_MESSAGES.cancelled)
    }
    if (typeof domError.message === 'string' && domError.message.trim()) {
      return domError.message
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message
  }

  return fallback
}
